/**
 * Órgãos e clientes (17 §3.1; 11 §A; D61, D62): cadastro único, público e privado.
 * - CNPJ consultado automaticamente; a esfera é sugerida pela natureza jurídica informada pela Receita Federal.
 * - Contatos e unidades compradoras (UASG / unidade no PNCP) ficam na própria ficha.
 */
import { useState, type ReactNode } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  BooleanField,
  BooleanInput,
  Create,
  Datagrid,
  Edit,
  Form,
  FunctionField,
  List,
  ResourceContextProvider,
  SaveButton,
  SelectField,
  SelectInput,
  SimpleForm,
  SimpleList,
  TextField,
  TextInput,
  email,
  required,
  useCreate,
  useCreateSuggestionContext,
  useDelete,
  useGetList,
  useNotify,
  usePermissions,
  useRecordContext,
  useRefresh,
  useUpdate,
} from "react-admin";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/EditOutlined";
import { formatarCep, formatarCnpj, formatarCpf, soDigitos } from "@sobral/dominio";
import { AcoesLista, BarraFormulario, EntradaTelefone, LinkWhatsapp, useCelular, vazioParaNulo } from "../componentes";
import { formatarTelefone, normalizarBusca } from "../lib/formato";
import type { Permissoes } from "../lib/provedores";
import { supabase } from "../lib/supabase";
import { consultarCnpj } from "../lib/cnpj";
import { ConsultaCnpj, UFS, type Preencher, type RespostaCnpj } from "./fornecedores";

export const TIPOS_PESSOA = [
  { id: "publico", name: "Órgão público" },
  { id: "privado", name: "Cliente privado" },
];

export const ESFERAS = [
  { id: "federal", name: "Federal" },
  { id: "estadual", name: "Estadual" },
  { id: "municipal", name: "Municipal" },
];

const formatarDocumento = (v: string | null | undefined) => {
  const d = soDigitos(v);
  if (d.length === 11) return formatarCpf(d);
  if (d.length === 14) return formatarCnpj(d);
  return v ?? "";
};

/**
 * Sugestão de tipo e esfera a partir da natureza jurídica (tabela da Receita Federal): códigos 1xx-x são da
 * administração pública; a esfera vem do próprio texto ("... Federal", "... Estadual ou do Distrito Federal", "... Municipal").
 */
function sugestaoPelaNatureza(dados: RespostaCnpj): { tipo: "publico" | "privado"; esfera: string | null } {
  const codigo = String(dados.codigo_natureza_juridica ?? "");
  const texto = normalizarBusca(dados.natureza_juridica ?? "");
  if (!codigo.startsWith("1")) return { tipo: "privado", esfera: null };
  if (texto.includes("municipal")) return { tipo: "publico", esfera: "municipal" };
  if (texto.includes("estadual") || texto.includes("distrito federal")) return { tipo: "publico", esfera: "estadual" };
  if (texto.includes("federal")) return { tipo: "publico", esfera: "federal" };
  return { tipo: "publico", esfera: null };
}

/**
 * Cadastra um órgão/cliente só com o CNPJ (dados públicos da Receita via BrasilAPI), com tipo e esfera sugeridos.
 * Usado no envio em lote de atestados. Devolve o registro criado.
 */
export async function criarPessoaPorCnpj(cnpj: string): Promise<{ id: string; nome: string }> {
  const resultado = await consultarCnpj(cnpj);
  if (resultado.situacao === "nao_encontrado") throw new Error("CNPJ não encontrado na Receita Federal.");
  if (resultado.situacao === "indisponivel") throw new Error(`Consulta à Receita indisponível (${resultado.detalhe}).`);
  const d = resultado.dados;
  const sugestao = sugestaoPelaNatureza(d);
  const logradouro = [d.descricao_tipo_de_logradouro, d.logradouro].filter(Boolean).join(" ").trim();
  const registro = {
    tipo: sugestao.tipo,
    esfera: sugestao.tipo === "publico" ? sugestao.esfera : null,
    documento: cnpj,
    nome: d.nome_fantasia || d.razao_social || cnpj,
    razao_social: d.razao_social ?? null,
    natureza_juridica: d.natureza_juridica ? `${d.codigo_natureza_juridica ?? ""} - ${d.natureza_juridica}`.trim() : null,
    logradouro: logradouro || null,
    numero: d.numero || null,
    complemento: d.complemento || null,
    bairro: d.bairro || null,
    municipio: d.municipio || null,
    uf: d.uf || null,
    cep: soDigitos(d.cep) || null,
    telefone: soDigitos(d.ddd_telefone_1) || null,
    email: d.email ? d.email.toLowerCase() : null,
  };
  const { data, error } = await supabase.from("pessoa").insert(registro).select("id, nome").single();
  if (error) throw new Error(error.message);
  return data as { id: string; nome: string };
}

/**
 * Janela de cadastro rápido de órgão/cliente (CNPJ consultado automaticamente na Receita).
 * Usada dentro de campos de escolha (CadastroRapidoPessoa) e nas linhas do envio de atestados em lote.
 */
export function DialogoCadastroPessoa({
  nomeInicial,
  documentoInicial,
  aoCancelar,
  aoCriar,
}: {
  nomeInicial?: string | undefined;
  documentoInicial?: string | null | undefined;
  aoCancelar: () => void;
  aoCriar: (pessoa: { id: string; nome: string }) => void;
}) {
  const [create] = useCreate();
  const notify = useNotify();
  const [erro, setErro] = useState<string | null>(null);

  async function gravar(valores: Record<string, unknown>) {
    setErro(null);
    try {
      const novo = (await create("pessoa", { data: { ...valores, ativo: true } }, { returnPromise: true })) as { id: string; nome: string };
      notify("Órgão cadastrado.", { type: "success" });
      aoCriar(novo);
    } catch (e) {
      setErro((e as Error).message.includes("pessoa_documento_key") ? "Já existe um órgão ou cliente com este CNPJ/CPF." : (e as Error).message);
    }
  }

  return (
    <Dialog open onClose={aoCancelar} fullWidth maxWidth="md">
      <ResourceContextProvider value="pessoa">
        <Form onSubmit={gravar} record={{ nome: nomeInicial ?? "", documento: documentoInicial ?? null, tipo: "publico", ativo: true }}>
          <DialogTitle>Cadastrar órgão ou cliente</DialogTitle>
          <DialogContent>
            {erro && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {erro}
              </Alert>
            )}
            <Box sx={{ pt: 1 }}>
              <CamposPessoa rapido consultarAoAbrir={Boolean(documentoInicial)} />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Contatos e unidades compradoras se cadastram na ficha do órgão.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={aoCancelar}>Cancelar</Button>
            <SaveButton label="Cadastrar e usar" alwaysEnable />
          </DialogActions>
        </Form>
      </ResourceContextProvider>
    </Dialog>
  );
}

/** Cadastro rápido de órgão/cliente sem sair do formulário atual (opção "Cadastrar órgão" dos campos de escolha). */
export function CadastroRapidoPessoa() {
  const { filter, onCancel, onCreate } = useCreateSuggestionContext();
  return <DialogoCadastroPessoa nomeInicial={filter} aoCancelar={onCancel} aoCriar={onCreate} />;
}

function useConsultaPessoa() {
  const { setValue, getValues } = useFormContext();
  return (dados: RespostaCnpj, preencher: Preencher) => {
    const logradouro = [dados.descricao_tipo_de_logradouro, dados.logradouro].filter(Boolean).join(" ").trim();
    preencher("logradouro", logradouro || null, "endereço");
    preencher("numero", dados.numero, "número");
    preencher("complemento", dados.complemento, "complemento");
    preencher("bairro", dados.bairro, "bairro");
    preencher("municipio", dados.municipio, "município");
    preencher("cep", soDigitos(dados.cep) || null, "CEP");
    if (dados.natureza_juridica) {
      setValue("natureza_juridica", `${dados.codigo_natureza_juridica ?? ""} - ${dados.natureza_juridica}`.trim(), { shouldDirty: true });
    }
    // Tipo e esfera: sugeridos sempre que a consulta traz a natureza jurídica (editáveis depois).
    const sugestao = sugestaoPelaNatureza(dados);
    setValue("tipo", sugestao.tipo, { shouldDirty: true });
    if (sugestao.tipo === "publico" && sugestao.esfera && !getValues("esfera")) {
      setValue("esfera", sugestao.esfera, { shouldDirty: true });
    }
    if (sugestao.tipo === "privado") setValue("esfera", null, { shouldDirty: true });
  };
}

function CampoDocumento({ consultarAoAbrir = false, rapido = false }: { consultarAoAbrir?: boolean; rapido?: boolean }) {
  const preencherExtra = useConsultaPessoa();
  return (
    <ConsultaCnpj
      source="documento"
      aceitaCpf
      preencherExtra={preencherExtra}
      avisoSituacao="Confira antes de contratar."
      consultarAoAbrir={consultarAoAbrir}
      // No cadastro rápido, o nome veio do texto da busca: a Receita corrige nome, razão social e UF.
      {...(rapido ? { sobrescrever: ["nome", "razao_social", "uf"] } : {})}
    />
  );
}

function CampoEsfera() {
  const tipo = useWatch({ name: "tipo" }) as string | undefined;
  if (tipo !== "publico") return null;
  return <SelectInput source="esfera" choices={ESFERAS} emptyText="Não informada" fullWidth />;
}

/* ------------------------------------ Lista ------------------------------------ */

const filtros = [
  <TextInput key="busca" source="busca@ilike" label="Pesquisar nome, razão social, CNPJ ou município" parse={normalizarBusca} alwaysOn resettable sx={{ minWidth: 300 }} />,
  <SelectInput key="tipo" source="tipo" label="Tipo" choices={TIPOS_PESSOA} />,
  <SelectInput key="esfera" source="esfera" label="Esfera" choices={ESFERAS} />,
  <SelectInput key="uf" source="uf" label="UF" choices={UFS} />,
  <SelectInput
    key="ativo"
    source="ativo"
    label="Situação"
    choices={[
      { id: true, name: "Ativos" },
      { id: false, name: "Inativos" },
    ]}
  />,
];

export function PessoaLista() {
  const celular = useCelular();
  return (
    <List title="Órgãos e clientes" sort={{ field: "nome", order: "ASC" }} filters={filtros} filterDefaultValues={{ ativo: true }} actions={<AcoesLista />} exporter={false}>
      {celular ? (
        <SimpleList
          primaryText={(r) => r.nome}
          secondaryText={(r) => [r.tipo === "publico" ? "Órgão público" : "Cliente privado", r.municipio && `${r.municipio}/${r.uf ?? ""}`].filter(Boolean).join(" · ")}
          linkType="edit"
        />
      ) : (
        <Datagrid bulkActionButtons={false} rowClick="edit">
          <TextField source="nome" />
          <FunctionField source="documento" render={(r) => (r.documento ? formatarDocumento(r.documento) : "—")} />
          <SelectField source="tipo" choices={TIPOS_PESSOA} />
          <SelectField source="esfera" choices={ESFERAS} emptyText="—" />
          <FunctionField label="Município" render={(r) => (r.municipio ? `${r.municipio}/${r.uf ?? ""}` : "—")} />
          <FunctionField source="telefone" render={(r) => (r.telefone ? formatarTelefone(r.telefone) : "—")} />
          <BooleanField source="ativo" />
        </Datagrid>
      )}
    </List>
  );
}

/* ----------------------------------- Formulário ----------------------------------- */

const meia = { xs: 12, sm: 6 } as const;

/** Sigla da pasta (D104): sem acento, maiúsculas, espaços e símbolos viram hífen. */
const normalizarSigla = (v: string) =>
  v
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-+/, "")
    .slice(0, 24);
const terco = { xs: 12, sm: 4 } as const;

/** Campos do órgão/cliente: os mesmos na ficha e no cadastro rápido (D112, paridade). */
function CamposPessoa({ rapido = false, consultarAoAbrir = false }: { rapido?: boolean; consultarAoAbrir?: boolean } = {}) {
  return (
    <Grid container columnSpacing={2} sx={{ width: "100%" }}>
      <Grid size={{ xs: 12, md: 6 }}>
        <CampoDocumento rapido={rapido} consultarAoAbrir={consultarAoAbrir} />
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <SelectInput source="tipo" choices={TIPOS_PESSOA} validate={required()} fullWidth />
      </Grid>
      <Grid size={{ xs: 6, md: 3 }}>
        <CampoEsfera />
      </Grid>
      <Grid size={meia}>
        <TextInput source="nome" validate={required()} fullWidth helperText="Nome usual (ex.: EBSERH-SE, Câmara de Aracaju)." />
      </Grid>
      <Grid size={{ xs: 12, sm: 4, md: 3 }}>
        <TextInput
          source="sigla"
          parse={(v: string) => (v ? normalizarSigla(v) : null)}
          fullWidth
          validate={(v: unknown) => (v && !/^[A-Z0-9][A-Z0-9-]{0,23}$/.test(String(v)) ? "Até 24 caracteres: letras, números e hífen." : undefined)}
          helperText="Nome da pasta no OneDrive (ex.: UFOB, CRISTOPOLIS). Vazia: o sistema sugere pelo nome."
        />
      </Grid>
      <Grid size={meia}>
        <TextInput source="razao_social" parse={vazioParaNulo} fullWidth />
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        <TextInput source="natureza_juridica" parse={vazioParaNulo} fullWidth helperText="Preenchida pela consulta do CNPJ." />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextInput source="inscricao_estadual" parse={vazioParaNulo} fullWidth />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextInput source="logradouro" parse={vazioParaNulo} fullWidth />
      </Grid>
      <Grid size={{ xs: 4, sm: 2 }}>
        <TextInput source="numero" parse={vazioParaNulo} fullWidth />
      </Grid>
      <Grid size={{ xs: 8, sm: 4 }}>
        <TextInput source="complemento" parse={vazioParaNulo} fullWidth />
      </Grid>
      <Grid size={terco}>
        <TextInput source="bairro" parse={vazioParaNulo} fullWidth />
      </Grid>
      <Grid size={{ xs: 8, sm: 4 }}>
        <TextInput source="municipio" parse={vazioParaNulo} fullWidth />
      </Grid>
      <Grid size={{ xs: 4, sm: 2 }}>
        <SelectInput source="uf" choices={UFS} emptyText="—" fullWidth sx={{ minWidth: 0 }} />
      </Grid>
      <Grid size={{ xs: 12, sm: 2 }}>
        <TextInput
          source="cep"
          format={formatarCep}
          parse={(v: string) => {
            const d = soDigitos(v);
            return d === "" ? null : d;
          }}
          fullWidth
        />
      </Grid>
      <Grid size={terco}>
        <EntradaTelefone source="telefone" fullWidth />
      </Grid>
      <Grid size={terco}>
        <TextInput source="email" parse={vazioParaNulo} validate={email("E-mail inválido.")} fullWidth />
      </Grid>
      <Grid size={terco}>
        <TextInput source="site" parse={vazioParaNulo} fullWidth />
      </Grid>
      <Grid size={12}>
        <TextInput source="observacao" parse={vazioParaNulo} multiline fullWidth />
      </Grid>
      {!rapido && (
        <Grid size={12}>
          <BooleanInput source="ativo" defaultValue={true} helperText="Inativo deixa de aparecer nas listas de escolha." />
        </Grid>
      )}
    </Grid>
  );
}

export function PessoaCriar() {
  return (
    <Create title="Novo órgão ou cliente" redirect="edit">
      <SimpleForm defaultValues={{ tipo: "publico", ativo: true }}>
        <Alert severity="info" sx={{ mb: 1, width: "100%" }}>
          Contatos e unidades compradoras são cadastrados na ficha, depois de salvar.
        </Alert>
        <CamposPessoa />
      </SimpleForm>
    </Create>
  );
}

/* ------------------------ Contatos e unidades (tabelas filhas) ------------------------ */

interface Contato {
  id: string;
  pessoa_id: string;
  nome: string;
  cargo: string | null;
  email: string | null;
  telefone: string | null;
  whatsapp: string | null;
  observacao: string | null;
  ativo: boolean;
}

interface Unidade {
  id: string;
  pessoa_id: string;
  codigo: string | null;
  nome: string;
  municipio: string | null;
  uf: string | null;
  ativo: boolean;
}

/** Diálogo de inclusão/edição de um registro filho (contato ou unidade). */
function DialogoFilho({
  recurso,
  titulo,
  registro,
  pessoaId,
  aoFechar,
  children,
}: {
  recurso: "pessoa_contato" | "unidade_compradora";
  titulo: string;
  registro: Record<string, unknown> | null;
  pessoaId: string;
  aoFechar: () => void;
  children: ReactNode;
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [create] = useCreate();
  const [update] = useUpdate();
  const [erro, setErro] = useState<string | null>(null);
  if (!registro) return null;
  const novo = !registro.id;

  function gravar(valores: Record<string, unknown>) {
    setErro(null);
    const opcoes = {
      onSuccess: () => {
        notify("Registro salvo.", { type: "success" });
        refresh();
        aoFechar();
      },
      onError: (e: unknown) => setErro((e as Error).message),
    };
    if (novo) create(recurso, { data: { ...valores, pessoa_id: pessoaId } }, opcoes);
    else update(recurso, { id: registro?.id as string, data: valores, previousData: registro ?? {} }, opcoes);
  }

  return (
    <Dialog open onClose={aoFechar} fullWidth maxWidth="sm">
      <Form record={registro} onSubmit={gravar} resource={recurso}>
        <DialogTitle>{titulo}</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ pt: 1 }}>
            {erro && <Alert severity="error">{erro}</Alert>}
            {children}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={aoFechar}>Cancelar</Button>
          <SaveButton label="Salvar" alwaysEnable={novo} />
        </DialogActions>
      </Form>
    </Dialog>
  );
}

function BotaoExcluirFilho({ recurso, id }: { recurso: string; id: string }) {
  const { permissions } = usePermissions<Permissoes | null>();
  const [remover] = useDelete();
  const notify = useNotify();
  const refresh = useRefresh();
  if (permissions?.papel !== "admin") return null;
  return (
    <Tooltip title="Excluir">
      <IconButton
        size="small"
        onClick={() =>
          remover(
            recurso,
            { id, previousData: { id } },
            {
              onSuccess: () => {
                notify("Registro excluído.", { type: "info" });
                refresh();
              },
              onError: (e) => notify((e as Error).message, { type: "error" }),
            },
          )
        }
        aria-label="Excluir"
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

function SecaoContatos({ pessoaId }: { pessoaId: string }) {
  const [edicao, setEdicao] = useState<Record<string, unknown> | null>(null);
  const { data: contatos = [] } = useGetList<Contato>("pessoa_contato", {
    filter: { pessoa_id: pessoaId },
    pagination: { page: 1, perPage: 100 },
    sort: { field: "nome", order: "ASC" },
  });
  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontSize: "1rem" }}>
            Contatos
          </Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setEdicao({ ativo: true })}>
            Adicionar contato
          </Button>
        </Stack>
        {contatos.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhum contato cadastrado.
          </Typography>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Cargo / setor</TableCell>
                  <TableCell>WhatsApp</TableCell>
                  <TableCell>Telefone</TableCell>
                  <TableCell>E-mail</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {contatos.map((c) => (
                  <TableRow key={c.id} sx={{ opacity: c.ativo ? 1 : 0.5 }}>
                    <TableCell>{c.nome}</TableCell>
                    <TableCell>{c.cargo ?? "—"}</TableCell>
                    <TableCell>
                      <LinkWhatsapp numero={c.whatsapp} />
                    </TableCell>
                    <TableCell>{c.telefone ? formatarTelefone(c.telefone) : "—"}</TableCell>
                    <TableCell>{c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : "—"}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <IconButton size="small" onClick={() => setEdicao(c as unknown as Record<string, unknown>)} aria-label="Editar">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <BotaoExcluirFilho recurso="pessoa_contato" id={c.id} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
      <DialogoFilho recurso="pessoa_contato" titulo={edicao?.id ? "Editar contato" : "Novo contato"} registro={edicao} pessoaId={pessoaId} aoFechar={() => setEdicao(null)}>
        <TextInput source="nome" label="Nome" validate={required()} fullWidth />
        <TextInput source="cargo" label="Cargo ou setor" parse={vazioParaNulo} fullWidth helperText="Ex.: Pregoeiro, Setor de compras, Almoxarifado." />
        <EntradaTelefone source="whatsapp" label="WhatsApp" fullWidth />
        <EntradaTelefone source="telefone" label="Telefone" fullWidth />
        <TextInput source="email" label="E-mail" parse={vazioParaNulo} validate={email("E-mail inválido.")} fullWidth />
        <TextInput source="observacao" label="Observação" parse={vazioParaNulo} multiline fullWidth />
        <BooleanInput source="ativo" label="Ativo" />
      </DialogoFilho>
    </Card>
  );
}

function SecaoUnidades({ pessoaId }: { pessoaId: string }) {
  const [edicao, setEdicao] = useState<Record<string, unknown> | null>(null);
  const { data: unidades = [] } = useGetList<Unidade>("unidade_compradora", {
    filter: { pessoa_id: pessoaId },
    pagination: { page: 1, perPage: 100 },
    sort: { field: "nome", order: "ASC" },
  });
  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: "1rem" }}>
              Unidades compradoras
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Código UASG (Compras.gov.br) ou da unidade no PNCP. Um órgão pode ter várias.
            </Typography>
          </Box>
          <Button size="small" startIcon={<AddIcon />} onClick={() => setEdicao({ ativo: true })}>
            Adicionar unidade
          </Button>
        </Stack>
        {unidades.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhuma unidade cadastrada.
          </Typography>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Município</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {unidades.map((u) => (
                  <TableRow key={u.id} sx={{ opacity: u.ativo ? 1 : 0.5 }}>
                    <TableCell>{u.codigo ?? "—"}</TableCell>
                    <TableCell>{u.nome}</TableCell>
                    <TableCell>{u.municipio ? `${u.municipio}/${u.uf ?? ""}` : "—"}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <IconButton size="small" onClick={() => setEdicao(u as unknown as Record<string, unknown>)} aria-label="Editar">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <BotaoExcluirFilho recurso="unidade_compradora" id={u.id} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
      <DialogoFilho recurso="unidade_compradora" titulo={edicao?.id ? "Editar unidade" : "Nova unidade compradora"} registro={edicao} pessoaId={pessoaId} aoFechar={() => setEdicao(null)}>
        <TextInput
          source="codigo"
          label="Código (UASG / PNCP)"
          parse={(v: string) => {
            const d = soDigitos(v);
            return d === "" ? null : d;
          }}
          fullWidth
        />
        <TextInput source="nome" label="Nome da unidade" validate={required()} fullWidth />
        <TextInput source="municipio" label="Município" parse={vazioParaNulo} fullWidth />
        <SelectInput source="uf" label="UF" choices={UFS} emptyText="—" fullWidth />
        <BooleanInput source="ativo" label="Ativa" />
      </DialogoFilho>
    </Card>
  );
}

function SecoesFilhas() {
  const registro = useRecordContext<{ id: string }>();
  if (!registro) return null;
  return (
    <>
      <SecaoContatos pessoaId={registro.id} />
      <SecaoUnidades pessoaId={registro.id} />
    </>
  );
}

export function PessoaEditar() {
  return (
    <Edit title="Órgão ou cliente" mutationMode="pessimistic" redirect={false}>
      <SimpleForm toolbar={<BarraFormulario />}>
        <CamposPessoa />
      </SimpleForm>
      <SecoesFilhas />
    </Edit>
  );
}
